import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["title", "headerText", "descriptionText", "qualificationText", "generateButton", "status", "refinePrompt", "refineButton", "headerTextWrapper", "descriptionTextWrapper", "qualificationTextWrapper", "requisitionId", "sourceText", "sourceTextWrapper", "copyFromRequisitionButton"]
  static values = { 
    generateUrl: String,
    refineUrl: String,
    isGenerating: Boolean,
    isRefining: Boolean
  }

  connect() {
    this.syncJobTitle()
    this.checkRequisitionAvailability()
  }

  syncJobTitle() {
    // Suche nach dem Job-Titel-Feld im Formular
    const jobTitleField = document.querySelector('textarea[name="job[title]"]')
    if (jobTitleField && this.hasTitleTarget) {
      // Kopiere den Wert vom Job-Titel-Feld
      this.titleTarget.value = jobTitleField.value
      
      // Überwache Änderungen am Job-Titel-Feld
      jobTitleField.addEventListener('input', () => {
        this.titleTarget.value = jobTitleField.value
      })
    }
  }

  async generateDescription() {
    if (this.isGeneratingValue) {
      return
    }
    
    const title = this.titleTarget.value.trim()
    
    if (!title) {
      this.showError("Bitte geben Sie einen Job-Titel ein")
      return
    }

    this.setGenerating(true)
    this.showStatus("Generiere Stellenbeschreibung...")

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      
      // Suche nach der Requisition-ID im Formular
      const requisitionIdField = document.querySelector('input[name="job[requisition_id]"]')
      const requisitionId = requisitionIdField ? requisitionIdField.value : null
      
      // Hole Quelltext aus dem Rich Text Feld
      const sourceText = this.hasSourceTextTarget ? this.getCurrentContent('sourceText') : null
      
      const requestBody = { 
        title: title,
        requisition_id: requisitionId,
        source_text: sourceText
      }
      
      //console.log("🌐 Making API request to:", this.generateUrlValue)
      //console.log("📤 Request body:", requestBody)
      
      const response = await fetch(this.generateUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })
      
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error("❌ API Error Response:", errorText)
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
      }

      const data = await response.json()
      //console.log("📦 API response data:", data)
      
      // Fülle die Felder dynamisch mit Animation
      await this.fillFieldsWithAnimation(data)
      
      this.showStatus("Stellenbeschreibung erfolgreich generiert!")
      setTimeout(() => this.hideStatus(), 3000)
      
    } catch (error) {
      console.error('❌ Error generating job description:', error)
      this.showError(`Fehler beim Generieren der Stellenbeschreibung: ${error.message}`)
    } finally {
      this.setGenerating(false)
    }
  }

  async refineDescription() {
    if (this.isRefiningValue) {
      return
    }

    const refinePrompt = this.refinePromptTarget.value.trim()
    if (!refinePrompt) {
      this.showError("Bitte geben Sie einen Verfeinerungs-Prompt ein")
      return
    }

    // Sammle aktuellen Inhalt
    const currentContent = {
      header_text: this.getCurrentContent('headerText'),
      description_text: this.getCurrentContent('descriptionText'),
      qualification_text: this.getCurrentContent('qualificationText')
    }

    this.setRefining(true)
    this.showStatus("Verfeinere Stellenbeschreibung...")

    try {
      const response = await fetch(this.refineUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ 
          prompt: refinePrompt,
          current_content: currentContent
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      // Fülle die Felder mit verfeinerten Inhalten
      await this.fillFieldsWithAnimation(data)
      
      this.showStatus("Stellenbeschreibung erfolgreich verfeinert!")
      setTimeout(() => this.hideStatus(), 3000)
      
    } catch (error) {
      console.error('Error refining job description:', error)
      this.showError("Fehler beim Verfeinern der Stellenbeschreibung")
    } finally {
      this.setRefining(false)
    }
  }

  async fillFieldsWithAnimation(data) {
    console.log("🎯 fillFieldsWithAnimation called with data:", data)
    
    // Warte kurz, damit Trix-Editoren geladen sind
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Debug: Zeige alle verfügbaren Trix-Editoren
    const allTrixEditors = document.querySelectorAll('trix-editor')
    console.log("🔍 Alle verfügbaren Trix-Editoren:", allTrixEditors.length)
    allTrixEditors.forEach((editor, index) => {
      console.log(`  ${index}:`, editor, "Editor loaded:", !!editor.editor)
    })
    
    // Header Text - Trix Editor
    if (data.header_text && this.hasHeaderTextTarget) {
      console.log("📝 Setting header text:", data.header_text)
      const trixElement = this.findTrixElement(this.headerTextTarget)
      if (trixElement) {
        console.log("✅ Header Trix Editor gefunden:", trixElement)
        await this.setTrixContent(trixElement, data.header_text, 30)
        console.log("✅ Header Text eingefügt")
      } else {
        console.log("❌ Could not find Trix element for header text")
        console.log("🔍 HeaderTextTarget:", this.headerTextTarget)
      }
    } else {
      console.log("⚠️ No header text or headerTextTarget not found")
    }

    // Description Text - Trix Editor
    if (data.description_text && this.hasDescriptionTextTarget) {
      //console.log("📝 Setting description text:", data.description_text)
      const trixElement = this.findTrixElement(this.descriptionTextTarget)
      if (trixElement) {
        //console.log("✅ Description Trix Editor gefunden:", trixElement)
        await this.setTrixContent(trixElement, data.description_text, 20)
        //console.log("✅ Description Text eingefügt")
      } else {
        //console.log("❌ Could not find Trix element for description text")
        //console.log("🔍 DescriptionTextTarget:", this.descriptionTextTarget)
      }
    } else {
      console.log("⚠️ No description text or descriptionTextTarget not found")
    }

    // Qualification Text - Trix Editor
    if (data.qualification_text && this.hasQualificationTextTarget) {
      console.log("📝 Setting qualification text:", data.qualification_text)
      const trixElement = this.findTrixElement(this.qualificationTextTarget)
      if (trixElement) {
        console.log("✅ Qualification Trix Editor gefunden:", trixElement)
        await this.setTrixContent(trixElement, data.qualification_text, 20)
        console.log("✅ Qualification Text eingefügt")
      } else {
        console.log("❌ Could not find Trix element for qualification text")
        console.log("🔍 QualificationTextTarget:", this.qualificationTextTarget)
      }
    } else {
      console.log("⚠️ No qualification text or qualificationTextTarget not found")
    }
  }

  findTrixElement(targetElement) {
    console.log("🔍 findTrixElement called with:", targetElement)
    
    // Suche nach Trix Editor in verschiedenen Varianten
    let trixElement = targetElement
    
    // 1. Direktes Element
    if (trixElement && trixElement.editor) {
      console.log("✅ Direktes Trix Element gefunden")
      return trixElement
    }
    
    // 2. Suche nach Trix Editor in Wrapper
    const wrapper = targetElement?.closest('.trix-wrapper')
    if (wrapper) {
      const trixEditor = wrapper.querySelector('trix-editor')
      if (trixEditor && trixEditor.editor) {
        console.log("✅ Trix Editor in Wrapper gefunden")
        return trixEditor
      }
    }
    
    // 3. Suche nach Trix Editor in der Nähe
    const nearbyTrix = targetElement?.parentElement?.querySelector('trix-editor')
    if (nearbyTrix && nearbyTrix.editor) {
      console.log("✅ Trix Editor in der Nähe gefunden")
      return nearbyTrix
    }
    
    // 4. Suche nach Trix Editor im gesamten Container
    const container = this.element
    const allTrixEditors = container.querySelectorAll('trix-editor')
    console.log("🔍 Suche in Container, gefunden:", allTrixEditors.length)
    
    for (let i = 0; i < allTrixEditors.length; i++) {
      const editor = allTrixEditors[i]
      console.log(`  ${i}: Editor loaded:`, !!editor.editor)
      if (editor.editor) {
        console.log("✅ Trix Editor im Container gefunden")
        return editor
      }
    }
    
    // 5. Fallback: Suche im gesamten Dokument
    const allTrixInDocument = document.querySelectorAll('trix-editor')
    console.log("🔍 Fallback: Suche im gesamten Dokument, gefunden:", allTrixInDocument.length)
    
    for (let i = 0; i < allTrixInDocument.length; i++) {
      const editor = allTrixInDocument[i]
      if (editor.editor) {
        console.log("✅ Trix Editor im Dokument gefunden")
        return editor
      }
    }
    
    console.log("❌ Kein Trix Editor gefunden")
    return null
  }

  async setTrixContent(trixElement, htmlContent, speed = 30) {
    return new Promise((resolve) => {
      // Warte bis Trix Editor geladen ist
      if (trixElement.editor) {
        this.typeTextIntoTrix(trixElement, htmlContent, speed, resolve)
      } else {
        // Warte auf Trix Editor Initialisierung
        const checkTrix = setInterval(() => {
          if (trixElement.editor) {
            clearInterval(checkTrix)
            this.typeTextIntoTrix(trixElement, htmlContent, speed, resolve)
          }
        }, 100)
        
        // Timeout nach 5 Sekunden
        setTimeout(() => {
          clearInterval(checkTrix)
          this.typeTextIntoTrix(trixElement, htmlContent, speed, resolve)
        }, 5000)
      }
    })
  }

  typeTextIntoTrix(trixElement, htmlContent, speed, resolve) {
    try {
      // Setze den Inhalt direkt in den Trix Editor
      trixElement.editor.loadHTML(htmlContent)
      resolve()
    } catch (error) {
      console.error("❌ Error in typeTextIntoTrix:", error)
      resolve()
    }
  }

  getCurrentContent(targetName) {
    const target = this[`${targetName}Target`]
    if (target) {
      const trixElement = this.findTrixElement(target)
      if (trixElement && trixElement.editor) {
        return trixElement.editor.getDocument().toString()
      }
    }
    return ""
  }

  clearFields() {
    // Leere alle Trix-Editoren
    if (this.hasHeaderTextTarget) {
      const trixElement = this.findTrixElement(this.headerTextTarget)
      if (trixElement && trixElement.editor) {
        trixElement.editor.loadHTML("")
      }
    }
    
    if (this.hasDescriptionTextTarget) {
      const trixElement = this.findTrixElement(this.descriptionTextTarget)
      if (trixElement && trixElement.editor) {
        trixElement.editor.loadHTML("")
      }
    }
    
    if (this.hasQualificationTextTarget) {
      const trixElement = this.findTrixElement(this.qualificationTextTarget)
      if (trixElement && trixElement.editor) {
        trixElement.editor.loadHTML("")
      }
    }
    
    this.showStatus("Felder geleert!")
    setTimeout(() => this.hideStatus(), 2000)
  }

  setGenerating(value) {
    this.isGeneratingValue = value
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = value
      this.generateButtonTarget.textContent = value ? "Generiere..." : "Stellenbeschreibung generieren"
    }
  }

  setRefining(value) {
    this.isRefiningValue = value
    if (this.hasRefineButtonTarget) {
      this.refineButtonTarget.disabled = value
      this.refineButtonTarget.textContent = value ? "Verfeinere..." : "Stellenbeschreibung verfeinern"
    }
  }

  showStatus(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.classList.remove('hidden')
    }
  }

  hideStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.add('hidden')
    }
  }

  showError(message) {
    this.showStatus(`❌ ${message}`)
    setTimeout(() => this.hideStatus(), 5000)
  }

  checkRequisitionAvailability() {
    // Prüfe ob eine Requisition-ID vorhanden ist
    const requisitionId = this.requisitionIdTarget ? this.requisitionIdTarget.value : null
    if (this.hasCopyFromRequisitionButtonTarget) {
      this.copyFromRequisitionButtonTarget.style.display = requisitionId ? 'block' : 'none'
    }
  }

  async copyFromRequisition() {
    if (!this.hasSourceTextTarget) {
      this.showError("Quelltext-Feld nicht gefunden")
      return
    }

    const requisitionId = this.requisitionIdTarget ? this.requisitionIdTarget.value : null
    if (!requisitionId || requisitionId.trim() === '') {
      this.showError("Keine Requisition-ID gefunden. Bitte wählen Sie zuerst eine Anfrage aus.")
      return
    }

    this.showStatus("Lade Requisition-Daten...")

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(`/backend/requisitions/${requisitionId}.json`, {
        method: 'GET',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        if (response.status === 404) {
          this.showError("Requisition nicht gefunden. Bitte überprüfen Sie die Requisition-ID.")
        } else if (response.status === 403) {
          this.showError("Keine Berechtigung zum Zugriff auf diese Requisition.")
        } else {
          this.showError(errorData.error || `Fehler beim Laden (Status: ${response.status})`)
        }
        return
      }

      const data = await response.json()
      
      if (data.error) {
        this.showError(data.error)
        return
      }
      
      const richDescription = data.rich_description || ""
      
      if (richDescription.trim() === '') {
        this.showError("Die Requisition enthält keinen Text zum Kopieren.")
        return
      }

      // Setze den Inhalt in den Rich Text Editor
      const trixElement = this.findTrixElement(this.sourceTextTarget)
      if (trixElement && trixElement.editor) {
        trixElement.editor.loadHTML(richDescription)
        this.showStatus("Requisition-Text erfolgreich kopiert!")
        setTimeout(() => this.hideStatus(), 3000)
      } else {
        this.showError("Rich Text Editor nicht gefunden")
      }
    } catch (error) {
      console.error('Error copying from requisition:', error)
      this.showError("Fehler beim Laden der Requisition-Daten: " + error.message)
    }
  }
}
